import type { SupabaseClient } from "@supabase/supabase-js";

import type { CyanOperationalSession } from "./cyanOperationalSessionService";
import type { CyanOperationalSessionStore } from "./cyanOperationalSessionStore";

/**
 * Durable B18 conversation-facing operational session.
 *
 * cyan_execution_programs remains mission/execution truth.
 * This store only persists the conversation-facing session required
 * to recover continuity after browser/process restart.
 */
export class SupabaseCyanOperationalSessionStore implements CyanOperationalSessionStore {
  constructor(private readonly supabase: SupabaseClient) {}

  async get(conversationId: string): Promise<CyanOperationalSession | null> {
    const { data, error } = await this.supabase
      .from("cyan_operational_sessions")
      .select("session")
      .eq("conversation_id", conversationId)
      .maybeSingle();

    if (error) {
      throw new Error(
        `Unable to load Cyan operational session: ${error.message}`,
      );
    }

    return data ? (data.session as CyanOperationalSession) : null;
  }

  async save(
    conversationId: string,
    session: CyanOperationalSession,
  ): Promise<void> {
    const { error } = await this.supabase
      .from("cyan_operational_sessions")
      .upsert(
        {
          conversation_id: conversationId,
          session,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "conversation_id",
        },
      );

    if (error) {
      throw new Error(
        `Unable to persist Cyan operational session: ${error.message}`,
      );
    }
  }

  async delete(conversationId: string): Promise<void> {
    const { error } = await this.supabase
      .from("cyan_operational_sessions")
      .delete()
      .eq("conversation_id", conversationId);

    if (error) {
      throw new Error(
        `Unable to delete Cyan operational session: ${error.message}`,
      );
    }
  }
}
